import { supabase } from '../supabase';
import type { CartLine } from '../types';

export interface PlaceOrderInput {
  tenantId: string;
  branchCode?: string;             // optional — RPC falls back to first active branch
  customerName: string;
  customerPhone: string;
  customerEmail?: string;          // required when verificationToken is set
  channel: 'website' | 'qr_table' | 'mobile';
  fulfillment: 'delivery' | 'pickup' | 'dine_in' | 'takeaway';
  tableNumber?: string;
  deliveryAddress?: string;
  notes?: string;
  lines: CartLine[];
  /** Guest checkout — single-use token from verify-customer-otp.
   *  When set, calls `place_customer_order_with_email_otp` and the token
   *  is consumed server-side as proof the email is verified. No
   *  Supabase Auth session needed — bypasses the built-in email
   *  rate limit. */
  verificationToken?: string;
}

export interface PlacedOrder {
  id: string;
  order_number: string;
}

export async function placeOrder(input: PlaceOrderInput): Promise<PlacedOrder> {
  if (input.lines.length === 0) throw new Error('cart is empty');

  const baseLines = input.lines.map((l) => ({
    menu_item_id: l.menu_item_id,
    quantity:     l.quantity,
    notes:        l.notes ?? null,
  }));

  // Guest checkout — verification token replaces the auth session.
  if (input.verificationToken) {
    if (!input.customerEmail) throw new Error('customerEmail required with a verification token');
    const { data, error } = await supabase.rpc('place_customer_order_with_email_otp', {
      p_tenant_id:           input.tenantId,
      p_branch_code:         input.branchCode ?? null,
      p_channel:             input.channel,
      p_type:                input.fulfillment,
      p_customer_name:       input.customerName,
      p_customer_phone:      input.customerPhone,
      p_customer_email:      input.customerEmail,
      p_table_number:        input.tableNumber ?? null,
      p_address:             input.deliveryAddress ?? null,
      p_notes:               input.notes ?? null,
      p_lines:               baseLines,
      p_verification_token:  input.verificationToken,
    });
    if (error) throw error;
    const row = Array.isArray(data) ? data[0] : data;
    if (!row?.order_id) throw new Error('Order placement returned no row');
    return { id: row.order_id, order_number: row.order_number };
  }

  // Authenticated customer path — uses auth.uid() from the session.
  const { data, error } = await supabase.rpc('place_customer_order', {
    p_tenant_id:     input.tenantId,
    p_branch_code:   input.branchCode ?? null,
    p_channel:       input.channel,
    p_type:          input.fulfillment,
    p_customer_name: input.customerName,
    p_customer_phone: input.customerPhone,
    p_table_number:  input.tableNumber ?? null,
    p_address:       input.deliveryAddress ?? null,
    p_notes:         input.notes ?? null,
    p_lines:         baseLines,
  });

  if (error) throw error;

  const row = Array.isArray(data) ? data[0] : data;
  if (!row?.order_id) throw new Error('Order placement returned no row');

  return { id: row.order_id, order_number: row.order_number };
}

export function subscribeToOrder(
  orderId: string,
  onChange: (newStatus: string) => void,
) {
  const channel = supabase
    .channel(`order_${orderId}`)
    .on(
      'postgres_changes',
      { event: 'UPDATE', schema: 'public', table: 'orders', filter: `id=eq.${orderId}` },
      (payload: any) => {
        const status = payload?.new?.status;
        if (status) onChange(status);
      },
    )
    .subscribe();
  return () => { void supabase.removeChannel(channel); };
}
